/**
 * The two milestone-ladder visuals shared by the sales cards and the Team Room
 * setup page's live preview (one source, so the preview IS the widget):
 *
 *  - ClimbLadder: the ladder as steps up a mountain — cleared steps filled green
 *    with their pay reading "✓", the rung in play tinted brand, a flag planted
 *    on the summit step, and the climber (avatar dot + YTD bubble) standing
 *    where their revenue puts them. Steps rise on mount; the climber pulses.
 *  - TrackLadder: the same ladder as stops on one road — a rail with tier
 *    nodes and the fill running through the cleared ones up to where the rep
 *    stands (no floating YTD bubble: it collided with the tier labels).
 *
 * Both take the ladder as [{ target, bonus }] (ascending) and a YTD number —
 * no fetching, no roles — so any surface can render them from any config.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { C, money, compact, compactPay } from "./common";
import { GET_IMAGE } from "../../helpers/images";

const GREEN = "#12A150";
const GREEN_TX = "#0F8A50";

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * "At this pace, $250K lands in late September" — projected purely from YTD:
 * run-rate = ytd / months elapsed this year, walked forward to the next rung.
 * Returns e.g. "late September" (+" 2027" when it slips past this year), or
 * null when there's no pace to project (no revenue yet, or ladder finished).
 */
export function paceFor(ytd, next, now = new Date()) {
  if (!next || !(ytd > 0)) return null;
  const monthsElapsed = now.getMonth() + 1;
  const rate = ytd / monthsElapsed;
  if (rate <= 0) return null;
  const monthsToGo = (next.target - ytd) / rate;
  if (!Number.isFinite(monthsToGo) || monthsToGo > 36) return null;
  const nowFloat = now.getMonth() + Math.min(0.97, (now.getDate() - 1) / 30);
  const landFloat = nowFloat + monthsToGo;
  const landMonth = Math.floor(landFloat);
  const frac = landFloat - landMonth;
  const land = new Date(now.getFullYear(), landMonth, 1);
  let part = "late";
  if (frac < 0.33) part = "early";
  else if (frac < 0.66) part = "mid";
  const name = land.toLocaleString("en-US", { month: "long" });
  const year = land.getFullYear() !== now.getFullYear() ? ` ${land.getFullYear()}` : "";
  return `${part} ${name}${year}`;
}

// Confetti burst at an SVG user-space point — maps user units to on-screen px
// through the (meet, xMid/yMax) fit so the burst lands exactly on the step.
function burstAt(wrap, svgRect, W, H, ux, uy) {
  if (!wrap) return;
  const scale = Math.min(svgRect.width / W, svgRect.height / H);
  const px = (svgRect.width - W * scale) / 2 + ux * scale;
  const py = svgRect.height - H * scale + uy * scale;
  const host = document.createElement("div");
  host.style.cssText = "position:absolute; inset:0; pointer-events:none; overflow:visible;";
  wrap.appendChild(host);
  const colors = ["#1B598E", "#12A150", "#E8A93B", "#EC2786", "#7FB3E3"];
  for (let i = 0; i < 26; i += 1) {
    const sp = document.createElement("span");
    const sz = 4 + Math.random() * 4;
    sp.style.cssText = `position:absolute; left:${px}px; top:${py}px; width:${sz}px; height:${sz * 0.6}px; background:${colors[i % 5]}; border-radius:1px;`;
    host.appendChild(sp);
    sp.animate(
      [
        { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
        { transform: `translate(${(Math.random() - 0.5) * 150}px, ${50 + Math.random() * 110}px) rotate(${(Math.random() - 0.5) * 540}deg)`, opacity: 0 },
      ],
      { duration: 900 + Math.random() * 700, easing: "cubic-bezier(.2,.6,.3,1)", fill: "forwards" }
    );
  }
  setTimeout(() => host.remove(), 1800);
}

const tipFor = (t, ytd, done, next) => {
  if (done) return `${compact(t.target)} cleared · ${money(t.bonus)} reached`;
  if (next) return `${money(t.target - ytd)} to go · pays +${money(t.bonus)}`;
  return `${compact(t.target)} · pays ${money(t.bonus)}`;
};

const payColor = (done, next, primary) => {
  if (done) return GREEN_TX;
  if (next) return primary;
  return C.faint;
};

// No "+" on the rung being chased — the colour already marks it as the next
// prize, and the sign read like a delta rather than the bonus amount.
const payLabel = (t, done) => {
  if (done) return `${compactPay(t.bonus)} ✓`;
  return compactPay(t.bonus);
};

/**
 * Keyframes for the entrance/pulse/wave, injected ONCE into <head> and never
 * removed.
 *
 * They used to be rendered as JSX guarded by "is the tag already in the
 * document?". That guard is false on every render AFTER the first, so React
 * deleted the tag it had just inserted — the keyframes vanished from the
 * document and every element animating with `backwards` fill snapped to its
 * end state. On the Team Room editor, where each keystroke re-renders, that
 * showed up as the ladder glitching constantly. Keeping the stylesheet outside
 * React's render tree means nothing can take it away.
 */
const MOTION_CSS = `
      .gmbl-step{transform-box:fill-box; transform-origin:center bottom; animation:gmblRise .5s cubic-bezier(.2,.7,.3,1) backwards;}
      @keyframes gmblRise{from{transform:scaleY(0);} to{transform:scaleY(1);}}
      .gmbl-fade{animation:gmblFade .45s ease-out backwards;}
      @keyframes gmblFade{from{opacity:0;} to{opacity:1;}}
      .gmbl-flag{transform-box:fill-box; transform-origin:left center; animation:gmblWave 2.8s ease-in-out infinite;}
      @keyframes gmblWave{0%,100%{transform:skewY(0deg);} 50%{transform:skewY(-7deg);}}
      .gmbl-pulse{animation:gmblChase 1.8s ease-out infinite; border-radius:50%;}
      @keyframes gmblChase{0%{box-shadow:0 0 0 0 rgba(27,89,142,.4);} 70%{box-shadow:0 0 0 9px rgba(27,89,142,0);} 100%{box-shadow:0 0 0 0 rgba(27,89,142,0);}}
      .gmbl-wv{animation:gmblSwell var(--dur,7s) linear infinite; will-change:transform;}
      @keyframes gmblSwell{to{transform:translateX(var(--sh));}}
      @media (prefers-reduced-motion:reduce){
        .gmbl-step,.gmbl-fade{animation:none;}
        .gmbl-flag,.gmbl-pulse,.gmbl-wv{animation:none;}
      }
    `;

let motionInjected = false;
const injectMotion = () => {
  if (motionInjected || typeof document === "undefined") return;
  motionInjected = true;
  if (document.getElementById("gmb-ladder-motion")) return;
  const el = document.createElement("style");
  el.id = "gmb-ladder-motion";
  el.textContent = MOTION_CSS;
  document.head.appendChild(el);
};
injectMotion();

/** Steps up the mountain. Height scales with the card; SVG keeps proportions.
 *  `unlockTick` (increment to trigger) replays the chosen unlock moment: the
 *  climber walks to the next riser, hops up, the step flips green with a check
 *  and confetti — then `onUnlockDone` fires so the card can reset to reality.
 *  No-ops (done fires immediately) at the summit or under reduced motion. */
export function ClimbLadder({ ytd = 0, milestones = [], initials = "", primary = "#1B598E", unlockTick = 0, onUnlockDone = null }) {
  const T = milestones;
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const youRef = useRef(null);
  const busyRef = useRef(false);

  // The drawing's aspect follows the card's, so the mountain FILLS the width —
  // no letterboxed dead space at wide sizes, no shrunken staircase.
  //
  // Measured in a LAYOUT effect (before the browser paints) and never on timers:
  // the old version re-measured at 250ms and 1000ms, so every card visibly
  // re-laid-out its staircase twice after load — the "glitch" on page load.
  // Now the first painted frame already has the right geometry; only a real
  // window resize changes it afterwards.
  const [aspect, setAspect] = useState(2.6);
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el || !el.clientWidth || !el.clientHeight) return;
      const next = Math.max(1.8, Math.min(6.5, el.clientWidth / el.clientHeight));
      setAspect((prev) => (Math.abs(next - prev) > 0.02 ? next : prev));
    };
    measure();
    window.addEventListener("resize", measure);
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      try {
        ro = new ResizeObserver(measure);
        ro.observe(wrapRef.current);
      } catch (e) {
        ro = null;
      }
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const H = 200;
  const W = Math.round(H * aspect);
  const GROUND = 182;
  const RISE = Math.min(36, 140 / Math.max(1, T.length));
  const stepW = Math.min(150, (W - 130) / Math.max(1, T.length));
  // When steps hit their width cap on a very wide card, center the staircase.
  const startX = Math.max(52, Math.round((W - T.length * stepW - 40) / 2));
  const ni = T.findIndex((t) => ytd < t.target);
  const topY = (i) => GROUND - (i + 1) * RISE;

  // Label geometry derived from the STEP's own height. Fixed offsets (y+18 /
  // y+32) only fitted a 4-rung ladder: add a 5th and the pay line fell out the
  // bottom of its step, so more milestones visibly pushed the text down past
  // the step. Now both lines scale to the step, and when a step is too short
  // for two lines the pay drops out (the footer and tooltip still carry it)
  // rather than spilling.
  const lab = (() => {
    const f1 = Math.max(8.5, Math.min(12, RISE * 0.34));
    // Pay line runs 1px smaller than before (unrounded amounts are longer).
    const f2 = Math.max(6.5, Math.min(9, RISE * 0.29 - 1));
    const pad = Math.max(1.5, RISE * 0.09);
    const y1 = pad + f1;
    const y2 = y1 + pad + f2;
    const bothFit = y2 + f2 * 0.22 <= RISE;
    return {
      f1,
      f2,
      bothFit,
      // baselines are relative to each step's own top edge
      y1: bothFit ? y1 : RISE / 2 + f1 * 0.36,
      y2,
    };
  })();

  // climber: walking the last-cleared surface toward the next riser (or the
  // approach ground before the first step)
  let ax;
  let ay;
  if (ni === -1) {
    ax = startX + T.length * stepW - stepW / 2;
    ay = topY(T.length - 1);
  } else if (ni === 0) {
    const frac = Math.max(0, Math.min(1, ytd / T[0].target));
    ax = 8 + frac * (startX - 8);
    ay = GROUND;
  } else {
    const frac = (ytd - T[ni - 1].target) / (T[ni].target - T[ni - 1].target);
    const stepLeft = startX + (ni - 1) * stepW;
    ax = stepLeft + 13 + frac * (stepW - 22);
    // Keep the climber inside its own step so it never hangs over the next
    // step's border.
    ax = Math.min(ax, stepLeft + stepW - 16); // stay inside this step
    ay = topY(ni - 1);
  }
  // The summit flag is PLANTED on the top step: its pole base sits exactly on
  // that step's surface, inset from the right edge so the step is visibly under
  // it (and clear of the cleared-check that sits in that corner). Standing it
  // past the staircase — as this and the prototype used to — left the pole
  // ending in mid-air, which read as a flag hanging in space.
  const FLAG_H = 26;
  const fx = startX + T.length * stepW - 22;
  const fy = topY(T.length - 1) - FLAG_H;

  // The unlock moment (proto choreography): walk to the riser by 42%, hop up
  // by 68%, settle mid-step at 1700ms — then the step flips cleared, a check
  // pops, confetti bursts; at 4200ms the card is told to reset to reality.
  useEffect(() => {
    if (!unlockTick) return undefined;
    const done = () => onUnlockDone && onUnlockDone();
    if (busyRef.current || ni === -1 || !T.length || reducedMotion() || !youRef.current) {
      done();
      return undefined;
    }
    busyRef.current = true;
    const you = youRef.current;
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    const riserX = startX + ni * stepW;
    const y2 = topY(ni);
    const targetX = riserX + stepW / 2;
    you.animate(
      [
        { transform: "translate(0px,0px)" },
        { transform: `translate(${riserX - 12 - ax}px,0px)`, offset: 0.42 },
        { transform: `translate(${riserX + 16 - ax}px,${y2 - ay}px)`, offset: 0.68 },
        { transform: `translate(${targetX - ax}px,${y2 - ay}px)` },
      ],
      { duration: 1700, easing: "ease-in-out", fill: "forwards" }
    );
    const t1 = setTimeout(() => {
      const step = svg && svg.querySelector(`[data-step="${ni}"]`);
      if (step) {
        step.style.fill = "#EAF6F0";
        step.style.stroke = "#BFE3D2";
      }
      const pay = svg && svg.querySelector(`[data-pay="${ni}"]`);
      if (pay) {
        pay.setAttribute("fill", GREEN_TX);
        pay.textContent = `${compactPay(T[ni].bonus)} ✓`;
      }
      // The green fill + "✓" on the pay label carry the unlock; no corner badge
      // (it straddled the step edge). Confetti is the celebration.
      if (svg) burstAt(wrap, svg.getBoundingClientRect(), W, H, targetX, y2 - 34);
    }, 1700);
    const t2 = setTimeout(() => {
      busyRef.current = false;
      done();
    }, 4200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      busyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockTick]);

  if (!T.length) return null;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMax meet"
        style={{ display: "block", width: "100%", height: "100%" }}
        aria-hidden="true"
      >
        <line x1="4" y1={GROUND} x2={W - 4} y2={GROUND} stroke={C.line} strokeWidth="1.4" />
        {T.map((t, i) => {
          const x = startX + i * stepW;
          const y = topY(i);
          const done = ytd >= t.target;
          const next = ni === i;
          let face = "#F5F7FA";
          let edge = "#E7EBF1";
          if (done) {
            face = "#EAF6F0";
            edge = "#BFE3D2";
          } else if (next) {
            face = `${primary}14`;
            edge = `${primary}66`;
          }
          let payFill = "#AAB4C0";
          if (done) payFill = GREEN_TX;
          else if (next) payFill = primary;
          return (
            // Keyed by POSITION, not by target value: keying on the value meant
            // every keystroke in the Team Room editor changed the key, so React
            // tore the step down and rebuilt it — replaying its entrance
            // animation on each character. That was the flicker while typing.
            /* eslint-disable-next-line react/no-array-index-key */
            <g key={`step-${i}`}>
              <path
                className="gmbl-step"
                data-step={i}
                style={{ animationDelay: `${i * 90}ms` }}
                d={`M${x} ${GROUND} V${y} H${x + stepW} V${GROUND} Z`}
                fill={face}
                stroke={edge}
                strokeWidth="1.5"
              >
                <title>{tipFor(t, ytd, done, next)}</title>
              </path>
              <text
                className="gmbl-fade"
                style={{ animationDelay: `${i * 90 + 140}ms` }}
                x={x + stepW / 2}
                y={y + lab.y1}
                textAnchor="middle"
                fontSize={lab.f1}
                fontWeight="600"
                fill={done || next ? C.ink : C.faint}
                fontFamily="inherit"
              >
                {compact(t.target)}
              </text>
              {lab.bothFit && (
                <text
                  className="gmbl-fade"
                  data-pay={i}
                  style={{ animationDelay: `${i * 90 + 140}ms` }}
                  x={x + stepW / 2}
                  y={y + lab.y2}
                  textAnchor="middle"
                  fontSize={lab.f2}
                  fontWeight="600"
                  fill={payFill}
                  fontFamily="inherit"
                >
                  {payLabel(t, done, next)}
                </text>
              )}
              {/* No corner badge. It was centred ON the step's top-right corner, so
                  it straddled that edge, butted into the next step's border and
                  crowded the spot where the climber stands. "Cleared" is already
                  unmistakable: the step is filled green and its pay reads "$250 ✓". */}
            </g>
          );
        })}
        {/* summit flag — pole base lands on the top step's surface */}
        <line x1={fx} y1={fy} x2={fx} y2={fy + FLAG_H} stroke={C.faint} strokeWidth="2.2" strokeLinecap="round" />
        <path className="gmbl-flag" d={`M${fx} ${fy} h18 l-5.5 5.5 5.5 5.5 h-18 z`} fill={primary} />
        {/* the climber */}
        <g ref={youRef} className="gmbl-fade" style={{ animationDelay: "550ms" }}>
          <rect x={ax - 29} y={ay - 52} width="58" height="16" rx="5" fill={C.ink} />
          <text x={ax} y={ay - 40.5} textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#fff" fontFamily="inherit">
            {money(ytd)}
          </text>
          <line x1={ax} y1={ay - 36} x2={ax} y2={ay - 27} stroke={C.ink} strokeWidth="2" />
          <g className="gmbl-pulse">
            <circle cx={ax} cy={ay - 15} r="10.5" fill={primary} stroke="#fff" strokeWidth="2.4" />
          </g>
          <text x={ax} y={ay - 11.4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff" fontFamily="inherit">
            {initials}
          </text>
        </g>
      </svg>
    </div>
  );
}

/** Stops on one road: nodes on a rail, fill through the cleared ones. */

/**
 * The Tide — the ladder as stepped blocks that fill like a tank.
 *
 * Chosen over the staircase because it answers the only question the card is
 * really asked: how far into THIS rung am I? Everything untouched is quiet grey
 * hatch, everything banked is green hatch, and the one rung in play holds water
 * at exactly the level you have reached.
 *
 * Three things that matter in the drawing:
 *  - The crest is ONE seamless sine spanning twice the block, shifted by exactly
 *    one block width, so it loops forever with a single composited transform.
 *    No per-frame JS, and `prefers-reduced-motion` stops it dead.
 *  - Hatch means real-but-quiet; flat fill means focus. Only the live rung is
 *    flat, which is what makes your eye land on it.
 *  - Each block has a shaded side face, so the ladder reads as depth rather than
 *    as a bar chart.
 */
/* live → read it, cleared → recede, ahead → quietest. A helper rather than a
   nested ternary, which the lint rules reject for good reason. */
const tideFace = (b) => {
  if (b.done) return "green";
  if (b.live) return "live";
  return "ahead";
};

/* Target labels are the quiet scale — the live one is only a shade darker, so
   it never competes with the pay label above the block. On the dark TV the light
   greys invert to faint inks so they read on graphite instead of glowing. */
const tideLabelColour = (b, dark = false) => {
  if (dark) {
    if (b.live) return "rgba(236,237,237,.72)";
    if (b.done) return "rgba(236,237,237,.48)";
    return "rgba(236,237,237,.34)";
  }
  if (b.live) return "#6B7480";
  if (b.done) return "#AEB6C0";
  return "#CBD2DB";
};

/* The ladder blocks/hatches/side-faces are drawn with hardcoded light greys so
   the 3D reads on a white card. On the TV the board sits inside `.tv-board.tv-dark`;
   these SVG fills can't be reached by a class override (all rungs share one
   class, the state is what differs), so the visual detects that ancestor and
   swaps to a dark palette. No `.tv-dark` ancestor on the home page / Team Room
   preview, so light mode is untouched. */
const tidePalette = (dark, onTv) => {
  // Dark TV: the blocks were barely 5% white — a murky, unreadable staircase on
  // a wall. Push the ahead/live tiles and their 3D sides up so the steps read
  // from across the room, keep the green cleared rungs clearly ahead of them.
  if (dark) {
    return {
      greenRect: "rgba(60,198,138,.34)", greenLine: "rgba(60,198,138,.12)",
      aheadRect: "rgba(255,255,255,.13)", aheadLine: "rgba(255,255,255,.06)",
      sideGreen: "rgba(60,198,138,.44)", sideLive: "rgba(255,255,255,.20)", sideAhead: "rgba(255,255,255,.12)",
      liveFlat: "rgba(255,255,255,.15)", capAhead: "rgba(255,255,255,.30)",
      pillBg: "#26272A", pillBorder: "rgba(255,255,255,.12)",
    };
  }
  // Light on a TV: the home-page greys (#E9ECF1) wash out at distance. A punchier
  // light palette so the staircase reads on the wall — only when on a TV.
  if (onTv) {
    return {
      greenRect: "#C2E9D4", greenLine: "#E0F4EA",
      aheadRect: "#D4DBE6", aheadLine: "#E8ECF3",
      sideGreen: "#A9DAC0", sideLive: "#CBD4E0", sideAhead: "#C3CCD9",
      liveFlat: "#DAE1EB", capAhead: "#C3CCD9",
      pillBg: "#fff", pillBorder: "#E5E9EF",
    };
  }
  // Home page / Team Room preview — the original quiet look, untouched.
  return {
    greenRect: "#D7F0E3", greenLine: "#F2FAF6",
    aheadRect: "#E9ECF1", aheadLine: "#F7F8FA",
    sideGreen: "#C6E6D6", sideLive: "#E4E8ED", sideAhead: "#DFE4EA",
    liveFlat: "#F0F2F5", capAhead: "#DFE4EA",
    pillBg: "#fff", pillBorder: "#EFF2F6",
  };
};

export function TideLadder({ ytd = 0, milestones = [], initials = "", image = null, primary = "#1B598E", unlockTick = 0, onUnlockDone = null, onRungClick = null }) {
  const T = milestones;
  const wrapRef = useRef(null);
  const uid = useRef(`t${Math.random().toString(36).slice(2, 8)}`).current;
  const [hover, setHover] = useState(null);
  const [replay, setReplay] = useState(0); // 0 = reality, 1 = the rung filled

  // On the dark TV the ladder's light greys have to become dark tiles — detect
  // the `.tv-dark` ancestor once, pre-paint. Home page / preview stay light.
  const [dark, setDark] = useState(false);
  const [onTv, setOnTv] = useState(false);
  // Re-detect each render (guarded) so a live TV light/dark toggle re-paints the
  // ladder, not just the first mount. closest() is cheap.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    const d = !!(el && el.closest(".tv-dark"));
    const t = !!(el && el.closest(".tv-board"));
    setDark((p) => (p === d ? p : d));
    setOnTv((p) => (p === t ? p : t));
  });
  const PAL = tidePalette(dark, onTv);

  // Same measurement contract as the climb: pre-paint, never on timers.
  const [aspect, setAspect] = useState(2.6);
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el || !el.clientWidth || !el.clientHeight) return;
      const next = Math.max(1.8, Math.min(6.5, el.clientWidth / el.clientHeight));
      setAspect((prev) => (Math.abs(next - prev) > 0.02 ? next : prev));
    };
    measure();
    window.addEventListener("resize", measure);
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      try {
        ro = new ResizeObserver(measure);
        ro.observe(wrapRef.current);
      } catch (e) {
        ro = null;
      }
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const H = 200;
  const W = Math.round(H * aspect);
  const base = 182;
  const n = Math.max(1, T.length);
  const pad = 8;
  const laneW = (W - pad * 2) / n;
  const frontW = laneW * 0.78;
  const topOf = (i) => base - (base - 26) * (0.42 + 0.55 * (n === 1 ? 1 : i / (n - 1)));
  const xOf = (i) => pad + i * laneW;
  const ni = T.findIndex((t) => ytd < t.target);
  const liveIdx = ni === -1 ? T.length - 1 : ni;

  // "See an unlock": the water rises to the brim, the rung banks, then the card
  // is told to put reality back. A no-op at the summit and under reduced motion.
  useEffect(() => {
    if (!unlockTick) return undefined;
    if (ni === -1 || reducedMotion()) {
      onUnlockDone?.();
      return undefined;
    }
    setReplay(1);
    const t = setTimeout(() => {
      setReplay(0);
      onUnlockDone?.();
    }, 2600);
    return () => clearTimeout(t);
  }, [unlockTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // A seamless swell: four half-period arcs across 2× the block.
  const swell = (x, y, w, amp, close) => {
    const seg = w / 2;
    let d = `M${x} ${y}`;
    for (let i = 0; i < 4; i += 1) d += ` q ${seg / 2} ${i % 2 ? amp : -amp} ${seg} 0`;
    return close ? `${d} L${x + seg * 4} ${base + 20} L${x} ${base + 20} Z` : d;
  };

  const blocks = T.map((t, i) => {
    const x = xOf(i);
    const top = topOf(i);
    const last = i === n - 1;
    const fw = last ? laneW : frontW;
    const done = i < liveIdx || (replay && i === liveIdx);
    const live = i === liveIdx && !replay;
    const prev = i > 0 ? T[i - 1].target : 0;
    const frac = Math.min(1, Math.max(0, (ytd - prev) / Math.max(1, t.target - prev)));
    const fy = base - (base - top) * frac;
    const amp = Math.min(5.5, fw * 0.045);
    /* The climber's avatar. Was up to 15 units of a 200-tall frame — at the card's
       ~1.7x scale that painted a ~50px photo, big enough to cover the water line
       it is supposed to be riding. Smaller: it marks the spot, it isn't the
       subject. */
    const tr = Math.min(11, Math.max(7, fw * 0.115));
    // The climber rides the waterline — but a rep on $0 has the water at the
    // block's floor, which sank the circle through the bottom edge and onto the
    // target label underneath. It gets a minimum perch: never below the inside
    // of its own block. Anyone with progress is unaffected (fy is already higher).
    const cy = Math.min(fy, base - tr - 4);
    return { t, i, x, top, fw, last, done, live, fy, cy, amp, tr };
  });

  const pill = hover != null ? blocks[hover] : null;

  // Screen-space anchor for a block's brim (top-centre) and base — the svg maps
  // its [0..W, 0..H] viewBox straight onto wrapRef, so a rect read + ratio is
  // exact. Used to place the hover pill and the click popover in a body portal
  // so the card can never clip them.
  const anchorFor = (b) => {
    const el = wrapRef.current;
    if (!el || !b) return null;
    const r = el.getBoundingClientRect();
    return {
      cx: r.left + ((b.x + b.fw / 2) / W) * r.width,
      top: r.top + (b.top / H) * r.height,
      bottom: r.top + (base / H) * r.height,
    };
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", flex: "1 1 auto", minHeight: 0 }}>
      {pill && (
        <div
          // Clamp the pill inside the card. Centering it on the block
          // (translate -50%) pushed half of it past the card's edge on the
          // first/last rungs, where the ancestor's overflow:hidden clipped it.
          ref={(el) => {
            if (!el || !wrapRef.current) return;
            const wrapW = wrapRef.current.clientWidth;
            const center = ((pill.x + pill.fw / 2) / W) * wrapW;
            const left = Math.round(
              Math.min(Math.max(center - el.offsetWidth / 2, 4), Math.max(4, wrapW - el.offsetWidth - 4))
            );
            el.style.left = `${left}px`;
            el.style.visibility = "visible";
          }}
          style={{
            position: "absolute",
            left: 0,
            visibility: "hidden", // shown by the ref once clamped — no jump
            top: `${(pill.top / H) * 100}%`,
            transform: "translate(0, -115%)",
            pointerEvents: "none",
            zIndex: 3,
            whiteSpace: "nowrap",
            background: PAL.pillBg,
            border: `1px solid ${PAL.pillBorder}`,
            borderRadius: "999px",
            padding: "7px 14px",
            boxShadow: "0 6px 18px rgba(16,24,40,.16)",
            fontSize: "12px",
            color: C.ink,
          }}
        >
          {tipFor(pill.t, ytd, pill.done, pill.live)}
        </div>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: "block", width: "100%", height: "100%", fontFamily: '"DM Sans", sans-serif' }}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <pattern id={`${uid}hg`} width="9" height="9" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="9" height="9" fill={PAL.greenRect} />
            <line x1="0" y1="0" x2="0" y2="9" stroke={PAL.greenLine} strokeWidth="4.2" />
          </pattern>
          <pattern id={`${uid}hb`} width="9" height="9" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="9" height="9" fill={PAL.aheadRect} />
            <line x1="0" y1="0" x2="0" y2="9" stroke={PAL.aheadLine} strokeWidth="4.2" />
          </pattern>
          <linearGradient id={`${uid}tank`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#2FA96C" />
            <stop offset=".55" stopColor="#4FC489" />
            <stop offset="1" stopColor="#74D9A6" />
          </linearGradient>
          <linearGradient id={`${uid}glow`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#74D9A6" stopOpacity=".26" />
            <stop offset="1" stopColor="#74D9A6" stopOpacity="0" />
          </linearGradient>
          {blocks.filter((b) => b.live).map((b) => (
            <clipPath key={b.i} id={`${uid}tk`}>
              <rect x={b.x} y={b.top} width={b.fw} height={base - b.top} />
            </clipPath>
          ))}
        </defs>

        {blocks.map((b) => (
          // Key by block index, not target — two rungs can share a target and a
          // content key would silently drop one of them.
          <g key={b.i}>
            {!b.last && (
              <path
                d={`M${b.x + b.fw} ${b.top} L${xOf(b.i + 1)} ${topOf(b.i + 1)} L${xOf(b.i + 1)} ${base} L${b.x + b.fw} ${base} Z`}
                /* The lit side of the block. These greys were mid-tone (#D2D7DD /
                   #C6CBD2) and, being the largest flat area on the card, pulled
                   the eye onto the LOCKED rungs instead of onto where the rep
                   actually is. Lightened so the 3D still reads as depth without
                   weighing more than the live green. */
                fill={{ green: PAL.sideGreen, live: PAL.sideLive, ahead: PAL.sideAhead }[tideFace(b)]}
              />
            )}
            <rect
              className="gmbl-step"
              style={{ animationDelay: `${b.i * 70}ms` }}
              x={b.x}
              y={b.top}
              width={b.fw}
              height={base - b.top}
              fill={{ green: `url(#${uid}hg)`, live: PAL.liveFlat, ahead: `url(#${uid}hb)` }[tideFace(b)]}
            />

            {b.live && (
              <>
                <g clipPath={`url(#${uid}tk)`}>
                  <rect x={b.x} y={b.fy} width={b.fw} height={base - b.fy} fill={`url(#${uid}tank)`} />
                  <rect x={b.x} y={b.top} width={b.fw} height={b.fy - b.top} fill={`url(#${uid}glow)`} />
                  <path
                    className="gmbl-wv"
                    style={{ "--sh": `${-b.fw}px`, "--dur": "9s" }}
                    d={swell(b.x, b.fy + 1.5, b.fw, b.amp * 1.25, true)}
                    fill="#8FE0B8"
                    opacity=".55"
                  />
                  <path
                    className="gmbl-wv"
                    style={{ "--sh": `${-b.fw}px`, "--dur": "6s" }}
                    d={swell(b.x, b.fy, b.fw, b.amp, true)}
                    fill={`url(#${uid}tank)`}
                  />
                  <path
                    className="gmbl-wv"
                    style={{ "--sh": `${-b.fw}px`, "--dur": "6s" }}
                    d={swell(b.x, b.fy, b.fw, b.amp, false)}
                    fill="none"
                    stroke="#D8FAE9"
                    strokeWidth="2"
                    opacity=".95"
                  />
                </g>
                {/* the brim you are chasing */}
                <line x1={b.x} y1={b.top} x2={b.x + b.fw} y2={b.top} stroke={primary} strokeWidth="1.6" strokeDasharray="5 4" opacity=".55" />
                {/* the player at the waterline — their real photo when we have it
                    (same GET_IMAGE resolution the roster avatars use), the brand
                    monogram otherwise. */}
                <circle cx={b.x + b.fw * 0.5} cy={b.cy} r={b.tr} fill={primary} stroke="#fff" strokeWidth="2.6" />
                {image ? (
                  <>
                    <clipPath id={`${uid}av`}>
                      <circle cx={b.x + b.fw * 0.5} cy={b.cy} r={b.tr - 1.3} />
                    </clipPath>
                    <image
                      href={GET_IMAGE(image)}
                      x={b.x + b.fw * 0.5 - (b.tr - 1.3)}
                      y={b.cy - (b.tr - 1.3)}
                      width={(b.tr - 1.3) * 2}
                      height={(b.tr - 1.3) * 2}
                      clipPath={`url(#${uid}av)`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                ) : (
                  initials && (
                    <text
                      x={b.x + b.fw * 0.5}
                      y={b.cy + b.tr * 0.34}
                      textAnchor="middle"
                      fontSize={b.tr * 0.78}
                      fontWeight="700"
                      fill="#fff"
                      fontFamily="inherit"
                    >
                      {initials}
                    </text>
                  )
                )}
              </>
            )}

            {!b.live && (
              <rect
                x={b.x + b.fw * 0.36}
                y={b.top - 3.5}
                width={b.fw * 0.28}
                height="5"
                rx="2.5"
                fill={b.done ? GREEN : PAL.capAhead}
                opacity=".9"
              />
            )}

            {/* labels live OUTSIDE the svg — see the HTML layer below */}

            {/* one hit lane per rung, so hovering anywhere in the column explains
                it — and, when the card offers a history, clicking it opens who
                cleared this rung and when. */}
            <rect
              x={xOf(b.i)}
              y="0"
              width={laneW}
              height={H}
              fill="transparent"
              style={onRungClick ? { cursor: "pointer" } : undefined}
              onMouseEnter={() => setHover(b.i)}
              onClick={onRungClick ? () => onRungClick(b.t.target, b.i, anchorFor(b)) : undefined}
            />
          </g>
        ))}
      </svg>

      {/* ── labels, in real pixels ─────────────────────────────────────────
          The chart is drawn in a 200-unit-tall viewBox and stretched to the
          card (preserveAspectRatio="none"), so anything inside the svg is
          scaled by whatever that card happens to be — a "11px" label painted
          at 19px, and, because the horizontal and vertical scales are
          independent, the glyphs squashed sideways every time the customize
          drawer narrowed the board. Type cannot be typeset in a box that
          stretches. So the labels sit in HTML over the chart, positioned by the
          same percentages the tooltip already uses: 12px is 12px at every card
          width, and DM Sans keeps its proportions.

          Two different things are labelled, so they must not look alike: the
          PAY (money earned) leads, the TARGET (revenue to reach) steps back. */}
      {blocks.map((b) => (
        <div
          key={`lbl${b.i}`}
          className="gmbl-fade"
          style={{
            position: "absolute",
            left: `${((b.x + b.fw / 2) / W) * 100}%`,
            top: `${(b.top / H) * 100}%`,
            transform: "translate(-50%, calc(-100% - 7px))",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            fontSize: "12px",
            fontWeight: 600,
            color: payColor(b.done, b.live, primary),
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.01em",
          }}
        >
          {payLabel(b.t, b.done, b.live)}
        </div>
      ))}
      {blocks.map((b) => (
        <div
          key={`tgt${b.i}`}
          className="gmbl-fade"
          style={{
            position: "absolute",
            left: `${((b.x + b.fw / 2) / W) * 100}%`,
            top: `${(base / H) * 100}%`,
            transform: "translate(-50%, 7px)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            fontSize: "10.5px",
            fontWeight: 500,
            letterSpacing: "0.06em",
            color: tideLabelColour(b, dark),
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {compact(b.t.target)}
        </div>
      ))}
    </div>
  );
}

export function TrackLadder({ ytd = 0, milestones = [], primary = "#1B598E" }) {
  const T = milestones;
  // Detect the dark TV so the rail's white tier nodes become dark tiles (their
  // text/rail already tokenize through C / --tvd-*). Light everywhere else.
  const wrapRef = useRef(null);
  const [dark, setDark] = useState(false);
  useLayoutEffect(() => {
    setDark(!!(wrapRef.current && wrapRef.current.closest(".tv-dark")));
  }, []);
  const nodeBg = dark ? "#26272A" : "#fff";
  const nodeBorderIdle = dark ? "rgba(255,255,255,.18)" : "#DCE3EB";
  if (!T.length) return null;
  const pos = (i) => ((i + 0.5) / T.length) * 100;
  const ni = T.findIndex((t) => ytd < t.target);
  let youPct;
  if (ni === -1) youPct = 100;
  else if (ni === 0) youPct = pos(0) * Math.max(0, Math.min(1, ytd / T[0].target));
  else {
    const frac = (ytd - T[ni - 1].target) / (T[ni].target - T[ni - 1].target);
    youPct = pos(ni - 1) + frac * (pos(ni) - pos(ni - 1));
  }
  return (
    <div ref={wrapRef} style={{ position: "relative", height: "96px", margin: "0 12px" }} aria-hidden="true">
        <div style={{ position: "absolute", left: 0, right: 0, top: "50px", height: "6px", background: C.soft, borderRadius: 99 }} />
        <div style={{ position: "absolute", left: 0, top: "50px", width: `${youPct}%`, height: "6px", background: primary, borderRadius: 99 }} />
        {T.map((t, i) => {
          const done = ytd >= t.target;
          const next = ni === i;
          let border = nodeBorderIdle;
          if (done) border = GREEN;
          else if (next) border = primary;
          return (
            // Positional key, same reason as the climb's steps: keying on the
            // target value remounted a node on every keystroke while editing.
            /* eslint-disable-next-line react/no-array-index-key */
            <React.Fragment key={`node-${i}`}>
              <span
                style={{
                  position: "absolute",
                  left: `${pos(i)}%`,
                  top: "16px",
                  transform: "translateX(-50%)",
                  fontSize: "12px",
                  fontWeight: done || next ? 600 : 500,
                  color: done || next ? C.ink : C.faint,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {compact(t.target)}
              </span>
              <span
                className={next ? "gmbl-pulse" : undefined}
                style={{
                  position: "absolute",
                  left: `${pos(i)}%`,
                  top: "42px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? GREEN : nodeBg,
                  border: `2px solid ${border}`,
                }}
              >
                {done && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {next && <i style={{ width: "8px", height: "8px", borderRadius: "50%", background: primary, display: "block" }} />}
              </span>
              <span
                style={{
                  position: "absolute",
                  left: `${pos(i)}%`,
                  top: "72px",
                  transform: "translateX(-50%)",
                  fontSize: "9.5px",
                  fontWeight: done || next ? 600 : 500,
                  color: payColor(done, next, primary),
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {payLabel(t, done, next)}
              </span>
            </React.Fragment>
          );
        })}
        {/* No YTD bubble on the rail: it collided with the tier labels and the
            card's own hero number already states the figure. Position is read
            from how far the fill runs and which node is in play. */}
    </div>
  );
}
